import { useState } from "react"
import {
    AnalysisServiceGetPHQ8LocalShapResponsePayload,
    RankedSignalDto,
} from "pokal_api"
import EinflussfaktorenDetailsModal from "./EinflussfaktorenDetailsModal"
import {
    LayerStatement,
    PatientStatementContext,
    directionMark,
    featureIcon,
    featureLabel,
    generalStatementsForSignal,
    patientStatementsForSignal,
    statementColorClass,
} from "./shapUtils"

type Props = {
    localPayload: AnalysisServiceGetPHQ8LocalShapResponsePayload | null | undefined
}

function StatementLine({ statement }: { statement: LayerStatement }) {
    return (
        <span className={`text-sm ${statementColorClass(statement.type)}`}>
            {statement.text}
        </span>
    )
}

function SignalRow({
    signal,
    patientCtx,
}: {
    signal: RankedSignalDto
    patientCtx: PatientStatementContext
}) {
    const Icon = featureIcon(signal.groupKey)
    const mark = directionMark(signal)
    
    const general = generalStatementsForSignal(signal)
    const patient = patientStatementsForSignal(signal, patientCtx)

    return (
        <div className={"py-3 first:pt-1 last:pb-1"}>
            <div className={"flex items-center gap-2"}>
                <span
                    className={`text-base leading-none ${mark.colorClass}`}
                    aria-hidden="true"
                >
                    {mark.symbol}
                </span>
                <Icon className={"h-4 w-4 flex-shrink-0 text-gray-500"} />
                <span className={"text-sm font-semibold text-gray-800"}>
                    {featureLabel(signal.groupKey)}
                </span>
            </div>

            <div className={"mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 pl-8 text-sm"}>
                <span className={"text-xs uppercase tracking-wide text-gray-400"}>
                    Allgemein
                </span>
                <div className={"flex flex-col gap-0.5"}>
                    {general.map((line, idx) => (
                        <StatementLine key={idx} statement={line} />
                    ))}
                </div>

                <span className={"text-xs uppercase tracking-wide text-gray-400"}>
                    Patient
                </span>
                <div className={"flex flex-col gap-0.5"}>
                    {patient.rawValuesLine && (
                        <span className={"text-sm text-gray-700"}>
                            {patient.rawValuesLine}
                        </span>
                    )}
                    {patient.contributionLines.map((line, idx) => (
                        <span
                            key={idx}
                            className={`text-sm ${statementColorClass(line.type)}`}
                        >
                            → {line.text}
                        </span>
                        
                    ))}
                    {patient.contributionPrefix && (
                        <span className={"text-xs text-gray-500"}>
                            {patient.contributionPrefix}
                        </span>
                    )}
                </div>
            </div>
        </div>
    )
}

export default function EinflussfaktorenCard({ localPayload }: Props) {
    const [isModalOpen, setIsModalOpen] = useState(false)

    // Read the pre-derived signal list from the API.  Python already grouped
    // avg+std variants, ranked by patient impact, classified directions, and
    // detected sign-agreement
    const signals: RankedSignalDto[] = localPayload?.signals ?? []

    // Patient-statement context: latest instance's source date (for the
    // inline date range) and total instance count (for the "im Mittel der
    // letzten N Vorhersagen" prefix)
    const instances = localPayload?.instances ?? []
    const patientCtx: PatientStatementContext = {
        latestInstanceDate:
            instances.length > 0 ? (instances[instances.length - 1].date ?? null) : null,
        instanceCount: instances.length,
    }

    if (signals.length === 0) {
        // Backend returns instances=[] AND signals=[] when the data is too
        // stale to produce any current-or-future predictions.  Surface this
        // explicitly so the user knows the analysis is gated on fresh data.
        const isStaleDataState =
            localPayload !== null &&
            localPayload !== undefined &&
            instances.length === 0
        if (isStaleDataState) {
            return (
                <div
                    className={
                        "flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800"
                    }
                >
                    <span>
                        Die Datenbasis ist zu veraltet – für aktuelle Vorhersagen
                        sind keine Einflussfaktoren berechenbar. Bitte neue
                        Synchronisierungsdaten abwarten.
                    </span>
                </div>
            )
        }
        return (
            <div className={"rounded-lg bg-white p-4 text-sm text-gray-500"}>
                Keine Einflussfaktoren verfügbar.
            </div>
        )
    }

    return (
        <div className={"flex flex-col gap-3"}>
            <p className={"text-sm text-gray-600"}>
                Welche Werte beeinflussen die Vorhersage für diesen Patienten?
            </p>

            <div className={"divide-y divide-gray-100"}>
                {signals.map((signal) => (
                    <SignalRow
                        key={signal.groupKey}
                        signal={signal}
                        patientCtx={patientCtx}
                    />
                ))}
            </div>

            <div className={"flex justify-end"}>
                <button
                    type="button"
                    onClick={() => setIsModalOpen(true)}
                    className={"text-xs text-blue-600 hover:underline"}
                >
                    Technische Details anzeigen
                </button>
            </div>

            {isModalOpen && (
                <EinflussfaktorenDetailsModal
                    signals={signals}
                    onClose={() => setIsModalOpen(false)}
                />
            )}
        </div>
    )
}
